"use client";

/**
 * @file        src/components/ui/PrimaryButton.tsx
 * @description Single reusable primary/admin button. One visual style for the
 *              whole project — same look as the reviews AdminToolbar buttons.
 *              Consumers keep full behavioral control via native button props.
 * @universal   knowledge/universals/components.md#primarybutton
 * @rule        Base classes are the single source of the button's look — do NOT
 *              fork a second component for a new style; add a parameter here.
 */

import type { ButtonHTMLAttributes, ReactNode } from "react";

/** Verbatim AdminToolbar class string — the one canonical primary-button look. */
const BASE_CLASSES =
  "px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-hover transition";

type PrimaryButtonProps = {
  /** Button label/content, e.g. "+ YouTube" or "Перегенерировать обложку". */
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  /** Defaults to "button" so a PrimaryButton near a form never auto-submits. */
  type?: "button" | "submit" | "reset";
  /** Appended AFTER base classes (e.g. "mt-4"); never replaces the base look. */
  className?: string;
} & Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children" | "onClick" | "disabled" | "type" | "className"
>;

/**
 * Native <button> with the canonical primary style.
 * @output A <button> carrying the verbatim base classes plus any appended className.
 * @important type defaults to "button"; className appends, it does not override.
 */
export default function PrimaryButton({
  children,
  onClick,
  disabled,
  type = "button",
  className,
  ...rest
}: PrimaryButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={className ? `${BASE_CLASSES} ${className}` : BASE_CLASSES}
      {...rest}
    >
      {children}
    </button>
  );
}
